import { h, resolveComponent } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'
import { protectRoute } from '@/routeGuard'
import { logout } from '@/utils/auth'

import DefaultLayout from '@/layouts/DefaultLayout'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: DefaultLayout,
    redirect: '/dashboard',
    children: [
      {
        path: '/dashboard',
        name: 'Übersicht',
        component: () =>
          import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard.vue'),
        beforeEnter: protectRoute,
        meta: {
          requiredPermissions: [],
        },
      },
      {
        path: '/operations',
        name: 'Betrieb',

        meta: {
          requiredPermissions: ['section_operations_view'],
        },
        children: [
          {
            path: '/operations/transactions',
            name: 'Ladevorgänge',
            component: () => import('@/views/operations/Transactions.vue'),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_operations_view',
                'feature_transactions_current_view',
              ],
            },
          },
        ],
      },
      {
        path: '/configuration',
        name: 'Konfiguration',
        meta: {
          requiredPermissions: ['section_configuration_view'],
        },
        children: [
          {
            path: '/configuration/chargepoints',
            name: 'Ladepunkte',
            component: () =>
              import(
                /* webpackChunkName: "configuration/chargepoints" */ '@/views/configuration/Chargepoints.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_chargepoints_view',
              ],
            },
          },
          {
            path: '/configuration/idtoken',
            name: 'Token',
            component: () =>
              import(
                /* webpackChunkName: "configuration/idtoken" */ '@/views/configuration/IdToken.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_idtokens_view',
              ],
            },
          },
          {
            path: '/configuration/vehicles',
            name: 'Fahrzeuge',
            component: () =>
              import(
                /* webpackChunkName: "configuration/vehicles" */ '@/views/configuration/Vehicles.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_vehicles_view',
              ],
            },
          },
          {
            // Anlegen und Bearbeiten teilen sich eine Ansicht; die Neuanlage
            // steht vor der Detailroute, sonst liest der Router "new" als Id.
            path: '/configuration/vehicles/new',
            name: 'Fahrzeug anlegen',
            component: () =>
              import(
                /* webpackChunkName: "configuration/vehicle-detail" */ '@/views/configuration/VehicleDetail.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_vehicles_view',
                'action_vehicles_create',
              ],
            },
          },
          {
            path: '/configuration/vehicles/:vehicleId',
            name: 'Fahrzeug',
            component: () =>
              import(
                /* webpackChunkName: "configuration/vehicle-detail" */ '@/views/configuration/VehicleDetail.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_vehicles_view',
                'action_vehicles_update',
              ],
            },
          },
          {
            // Choosing a provider is one decision and stays a dialog; what the
            // chosen provider then needs - credential, unit system and which
            // entity fills which role - is one form, not three places.
            path: '/configuration/vehicles/:vehicleId/provider',
            name: 'API-Details',
            component: () =>
              import(
                /* webpackChunkName: "configuration/vehicle-provider" */ '@/views/configuration/VehicleProviderDetails.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_vehicles_view',
                'action_vehicles_update',
              ],
            },
          },
          {
            path: '/configuration/calendars',
            name: 'Kalender',
            component: () =>
              import(
                /* webpackChunkName: "configuration/calendars" */ '@/views/configuration/Calendars.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_calendars_view',
              ],
            },
          },
          {
            path: '/configuration/displays',
            name: 'Displays',
            component: () =>
              import(
                /* webpackChunkName: "configuration/displays" */ '@/views/configuration/DisplayManagement.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_configuration_view',
                'feature_displays_view',
              ],
            },
          },
        ],
      },
      {
        path: '/administration',
        name: 'Verwaltung',
        meta: {
          requiredPermissions: ['section_admin_view'],
        },
        children: [
          {
            path: '/administration/users',
            name: 'Benutzer',
            component: () =>
              import(
                /* webpackChunkName: "/administration/users" */ '@/views/administration/Users.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: ['section_admin_view', 'feature_users_view'],
            },
          },
          {
            path: '/administration/roles',
            name: 'Rollen',
            component: () =>
              import(
                /* webpackChunkName: "/administration/roles" */ '@/views/administration/Roles.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: ['section_admin_view', 'feature_roles_view'],
            },
          },
          {
            path: '/administration/permissions',
            name: 'Berechtigungen',
            component: () =>
              import(
                /* webpackChunkName: "/administration/permissions" */ '@/views/administration/Permissions.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_admin_view',
                'feature_permissions_view',
              ],
            },
          },
          {
            // Gruppen-Record mit Namen + Guard, damit der Breadcrumb auf der
            // Detailseite "Liegenschaften" als klickbaren Rueckweg zeigt
            path: '/administration/properties',
            name: 'Liegenschaften',
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [
                'section_admin_view',
                'feature_properties_view',
              ],
            },
            children: [
              {
                // Eigener Name, sonst warnt der Router: ein namenloses Kind mit
                // leerem Pfad ist ueber den Namen der Gruppe nicht erreichbar.
                // Im Breadcrumb steht es fuer dieselbe Seite wie die Gruppe und
                // bleibt deshalb ausgeblendet.
                path: '',
                name: 'Liegenschaftenliste',
                component: () =>
                  import(
                    /* webpackChunkName: "/administration/properties" */ '@/views/administration/Properties.vue'
                  ),
                beforeEnter: protectRoute,
                meta: {
                  requiredPermissions: [
                    'section_admin_view',
                    'feature_properties_view',
                  ],
                  hideInBreadcrumb: true,
                },
              },
              {
                path: ':id',
                name: 'Liegenschaft',
                component: () =>
                  import(
                    /* webpackChunkName: "/administration/property-detail" */ '@/views/administration/PropertyDetail.vue'
                  ),
                beforeEnter: protectRoute,
                meta: {
                  requiredPermissions: [
                    'section_admin_view',
                    'feature_properties_view',
                  ],
                },
              },
            ],
          },
          {
            path: '/administration/versions',
            name: 'Versionen',
            component: () =>
              import(
                /* webpackChunkName: "/administration/versions" */ '@/views/administration/SystemVersions.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: ['section_admin_view'],
            },
          },
          {
            path: '/administration/schema-validation',
            name: 'Schemavalidierung',
            component: () =>
              import(
                /* webpackChunkName: "/administration/schema-validation" */ '@/views/administration/SchemaValidation.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: ['section_admin_view'],
            },
          },
        ],
      },
      {
        path: '/settings',
        name: 'Einstellungen',
        meta: {
          requiredPermissions: [],
        },
        children: [
          {
            path: '/settings/authentication',
            name: 'Zugangsdaten',
            component: () =>
              import(
                /* webpackChunkName: "/settings/authentication" */ '@/views/settings/Authentication.vue'
              ),
            beforeEnter: protectRoute,
            meta: {
              requiredPermissions: [],
            },
          },
        ],
      },
    ],
  },
  {
    path: '/pages',
    redirect: '/pages/404',
    name: 'Pages',
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    children: [
      {
        path: '/login',
        name: 'Login',
        component: () =>
          import(/* webpackChunkName: "login" */ '@/views/Login.vue'),
      },
      {
        path: '/logout',
        name: 'Logout',
        beforeEnter: logout,
        component: () =>
          import(/* webpackChunkName: "logout" */ '@/views/Login.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
  prefetchLinks: false,
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

export default router
